import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  DiscoverAppCard,
  DiscoverAppCardSkeleton,
} from "../components/DiscoverAppCard";
import {
  IconChevronDown,
  IconSearch,
} from "../components/BenchmarkIcons";
import { fetchCategories, type Category } from "../services/apps";
import { resolveCategoryOptions } from "../services/categoryOptions";
import {
  fetchAllDiscoverApps,
  fetchPublishedInventoryCount,
  getDefaultDiscoverFilter,
  MARKETPLACE_INVENTORY_PLACEHOLDER_CARDS,
  shouldFetchDiscoverAppsResults,
  shouldShowMarketplaceInventoryPlaceholders,
  type DiscoverApp,
  type DiscoverAppsFilter,
  type DiscoverAppsPageMode,
} from "../services/discoverApps";

const filterOptions = [
  ["featured", "Featured"],
  ["trending", "Trending"],
  ["new", "New"],
  ["for_sale", "For sale"],
] as const satisfies ReadonlyArray<[DiscoverAppsFilter, string]>;

const DISCOVER_GRID_COLUMNS = 2;
const PLACEHOLDER_LAZY_BATCH = DISCOVER_GRID_COLUMNS * 2;

type DiscoverAppsPageProps = {
  mode?: DiscoverAppsPageMode;
};

export function DiscoverAppsPage({ mode = "discover" }: DiscoverAppsPageProps) {
  const [params, setParams] = useSearchParams();
  const [apps, setApps] = useState<DiscoverApp[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [searchInput, setSearchInput] = useState(params.get("q") ?? "");
  const [debouncedQuery, setDebouncedQuery] = useState(searchInput);
  const [filter, setFilter] = useState<DiscoverAppsFilter | "">(() =>
    getDefaultDiscoverFilter(mode, params.get("filter")),
  );
  const category = params.get("category") ?? "";
  const categoryOptions = useMemo(
    () => resolveCategoryOptions(categories),
    [categories],
  );
  const [isInitialLoading, setIsInitialLoading] = useState(() =>
    shouldFetchDiscoverAppsResults(mode, category),
  );
  const [isRefetching, setIsRefetching] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [publishedInventoryCount, setPublishedInventoryCount] = useState<
    number | null
  >(null);
  const hasLoadedResultsRef = useRef(false);
  const viewportRef = useRef<HTMLDivElement>(null);
  const [visiblePlaceholderCount, setVisiblePlaceholderCount] = useState(
    MARKETPLACE_INVENTORY_PLACEHOLDER_CARDS,
  );
  const showInventoryPlaceholders = shouldShowMarketplaceInventoryPlaceholders(
    publishedInventoryCount,
  );

  useEffect(() => {
    void fetchCategories()
      .then(setCategories)
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    let cancelled = false;

    void fetchPublishedInventoryCount()
      .then((count) => {
        if (!cancelled) {
          setPublishedInventoryCount(count);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPublishedInventoryCount(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedQuery(searchInput);
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchInput]);

  useEffect(() => {
    setParams((currentParams) => {
      const merged = new URLSearchParams(currentParams);
      const nextQuery = debouncedQuery.trim();

      if (nextQuery) {
        merged.set("q", nextQuery);
      } else {
        merged.delete("q");
      }

      return merged;
    });
  }, [debouncedQuery, setParams]);

  useEffect(() => {
    let cancelled = false;

    if (!shouldFetchDiscoverAppsResults(mode, category)) {
      setApps([]);
      setFetchError("");
      setIsInitialLoading(false);
      setIsRefetching(false);
      return;
    }

    const preserveExistingResults = hasLoadedResultsRef.current;
    if (preserveExistingResults) {
      setIsRefetching(true);
    } else {
      setIsInitialLoading(true);
    }
    setFetchError("");

    void fetchAllDiscoverApps({
      q: debouncedQuery,
      filter,
      category: category || undefined,
    })
      .then((nextApps) => {
        if (!cancelled) {
          setApps(nextApps);
          hasLoadedResultsRef.current = true;
        }
      })
      .catch(() => {
        if (!cancelled) {
          setApps([]);
          setFetchError("Unable to load apps right now. Please try again.");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsInitialLoading(false);
          setIsRefetching(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [mode, debouncedQuery, filter, category]);

  useEffect(() => {
    setVisiblePlaceholderCount(MARKETPLACE_INVENTORY_PLACEHOLDER_CARDS);
  }, [showInventoryPlaceholders, debouncedQuery, filter, category]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || !showInventoryPlaceholders) {
      return;
    }

    const appendPlaceholdersNearBottom = () => {
      const { scrollTop, clientHeight, scrollHeight } = viewport;
      if (scrollTop + clientHeight >= scrollHeight - 96) {
        setVisiblePlaceholderCount((count) => count + PLACEHOLDER_LAZY_BATCH);
      }
    };

    viewport.addEventListener("scroll", appendPlaceholdersNearBottom, {
      passive: true,
    });

    return () => {
      viewport.removeEventListener("scroll", appendPlaceholdersNearBottom);
    };
  }, [showInventoryPlaceholders]);

  function updateParams(next: Record<string, string>) {
    setParams((currentParams) => {
      const merged = new URLSearchParams(currentParams);

      Object.entries(next).forEach(([key, value]) => {
        if (value) {
          merged.set(key, value);
        } else {
          merged.delete(key);
        }
      });

      return merged;
    });
  }

  function renderGridContent() {
    // While inventory placeholders are active (including pending count), keep
    // the card grid mounted instead of a short loading message that collapses height.
    if (isInitialLoading && !showInventoryPlaceholders) {
      return (
        <p
          aria-live="polite"
          className="oac-discover-grid-message oac-discover-status--grid oac-discover-grid-message--loading"
        >
          Loading apps...
        </p>
      );
    }

    if (fetchError) {
      return (
        <p className="oac-discover-grid-message oac-discover-status--grid oac-discover-grid-message--error">
          {fetchError}
        </p>
      );
    }

    if (apps.length === 0 && !showInventoryPlaceholders) {
      return (
        <p className="oac-discover-grid-message oac-discover-status--grid oac-discover-empty--grid">
          No published apps found.
        </p>
      );
    }

    return (
      <>
        {apps.map((app) => (
          <DiscoverAppCard app={app} key={app.id} />
        ))}
        {showInventoryPlaceholders
          ? Array.from({ length: visiblePlaceholderCount }, (_, index) => (
              <DiscoverAppCardSkeleton key={`inventory-placeholder-${index}`} />
            ))
          : null}
      </>
    );
  }

  function renderColumnStatus() {
    // Discover Apps: never show "Loading apps..." under Search — it shifts the left panel.
    // Categories keeps the existing column loading status (already correct).
    if (isInitialLoading) {
      if (mode === "discover") {
        return null;
      }
      return (
        <p
          aria-live="polite"
          className="oac-discover-grid-message oac-discover-status--column oac-discover-grid-message--loading"
        >
          Loading apps...
        </p>
      );
    }

    if (fetchError) {
      return (
        <p className="oac-discover-grid-message oac-discover-status--column oac-discover-grid-message--error">
          {fetchError}
        </p>
      );
    }

    if (apps.length === 0 && !showInventoryPlaceholders) {
      return (
        <p className="oac-discover-grid-message oac-discover-status--column oac-discover-empty--column">
          No published apps found.
        </p>
      );
    }

    return null;
  }

  return (
    <section
      aria-labelledby="discover-title"
      className="oac-discover-layout"
      data-discover-mode={mode}
    >
      <div className="oac-discover-layout__filters">
        <h1
          className="oac-discover-headline oac-membership-page__heading"
          id="discover-title"
        >
          Discover Apps
        </h1>

        <p className="oac-discover-desc">
          Find apps created by independent developers around the world.
          Explore useful, innovative and unique Android apps.
        </p>

        <div className="oac-discover-filters">
          {filterOptions.map(([value, label], index) => (
            <span key={value}>
              <button
                className={`oac-discover-filter ${
                  filter === value ? "oac-discover-filter--active" : ""
                }`}
                data-discover-filter={value}
                onClick={() => {
                  const nextFilter = filter === value ? "" : value;
                  setFilter(nextFilter);
                  updateParams({ filter: nextFilter });
                }}
                type="button"
              >
                {label}
              </button>
              {index < filterOptions.length - 1 && (
                <span aria-hidden="true" className="oac-discover-sep">
                  |
                </span>
              )}
            </span>
          ))}
        </div>

        <label className="oac-discover-select">
          <select
            onChange={(event) =>
              updateParams({ category: event.target.value })
            }
            value={category}
          >
            <option disabled value="">
              Choose your app category
            </option>
            {categoryOptions.map((entry) => (
              <option key={entry.id} value={entry.slug}>
                {entry.name}
              </option>
            ))}
          </select>
          <IconChevronDown className="oac-discover-select-icon" />
        </label>

        <label className="oac-discover-search">
          <IconSearch />
          <input
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search"
            type="search"
            value={searchInput}
          />
        </label>

        {renderColumnStatus()}
      </div>

      <div className="oac-discover-layout__grid">
        <div
          className={`oac-discover-grid-viewport app-scroll${
            isRefetching ? " oac-discover-grid-viewport--refetching" : ""
          }`}
          ref={viewportRef}
        >
          <div className="oac-discover-grid">{renderGridContent()}</div>
        </div>
      </div>
    </section>
  );
}
